/**
 * @reflecto/core — filetransfer/types.ts
 *
 * File-transfer wire schema (architecture doc §3.1). These types travel INSIDE
 * the E2E envelope as `{ type: "file_ctrl", payload: FileCtrl }` and are
 * decrypted client-side only — the server never sees them.
 *
 * Unknown `kind` values are silently ignored (same rule as envelope `type`).
 * Readers must reject `v !== 1`.
 *
 * Forward compat: unknown FIELDS are tolerated, and the two closed enums this
 * wire carries (`error.code`, `decline.reason`) COERCE an unrecognised value to
 * their catch-all member rather than dropping the ctrl — see
 * `normalizeFtErrorCode` / `normalizeFtDeclineReason`.
 */

import { decodeBase64, decodeUTF8 } from "tweetnacl-util";

export const FT_PROTOCOL_VERSION = 1;
export const CHUNK_SIZE = 65536; // plaintext bytes per chunk
export const AEAD_OVERHEAD = 16; // XChaCha20-Poly1305 tag
export const CHUNK_CIPHER_SIZE = CHUNK_SIZE + AEAD_OVERHEAD; // 65552
export const CHUNKS_PER_PART = 80; // part ≈ 5.0 MiB
export const NONCE_PREFIX_LEN = 16;
export const MAX_FILES = 256; // per-transfer file-count cap (design §caps)

export interface TransferManifest {
  v: 1;
  transferId: string; // UUIDv4, client-generated, one-time-use
  senderDeviceId: string;
  recipientDeviceIds: string[]; // v1: exactly one entry
  files: FileEntry[]; // 1..MAX_FILES entries
  totalBytes: number; // sum of plaintext sizes
  chunkSize: number; // 65536; readers must honor manifest value
  cipher: "xchacha20poly1305-chunked-v1";
  noncePrefix: string; // base64, 16 bytes, random per transfer
  createdAt: number; // unix ms
}

export interface FileEntry {
  fileId: string; // UUIDv4
  name: string; // E2E only — never sent to server APIs
  mime: string;
  size: number; // plaintext bytes
  chunkCount: number; // ceil(size / chunkSize); 0-byte file => 1 empty final chunk
  sha256: string; // hex, whole-file plaintext hash
}

export type FileCtrl =
  // `protocolVersion` is RESERVED, not negotiated: nothing emits it yet and no
  // reader branches on it. It exists so a future negotiation change is a
  // sender-side change only — the guard already tolerates unknown fields (the
  // corpus pins that both ways), and giving the field a typed home now means
  // that future PR needs no guard edit and no wire-format change. Absent =
  // FT_PROTOCOL_VERSION (1). See docs/file-transfer-architecture.md §3's
  // 2026-07-29 forward-compat amendment.
  | { kind: "offer"; manifest: TransferManifest; protocolVersion?: number }
  | { kind: "accept"; transferId: string; resumeFromChunk?: number }
  | { kind: "decline"; transferId: string; reason: FtDeclineReason }
  // Delivery receipt (#808 item 6): the recipient auto-posts this the instant it
  // RECEIVES the offer and surfaces its accept/decline affordance — before the
  // human decides. Lets the sender's card distinguish "still delivering" (FCM/push
  // may lag minutes on a dozing phone) from "delivered, waiting on the tap".
  // Carries only transferId; additive, so older peers that never send it just
  // leave the sender on its initial "delivering" stage until accept/decline.
  | { kind: "offer_received"; transferId: string }
  | { kind: "progress"; transferId: string; fileId: string; chunksDone: number } // throttle ≥2s
  | { kind: "pause"; transferId: string }
  | { kind: "resume"; transferId: string }
  | { kind: "cancel"; transferId: string; reason: string }
  | { kind: "complete"; transferId: string; verified: boolean }
  | { kind: "error"; transferId: string; code: FtErrorCode; message: string }
  | { kind: "p2p_signal"; transferId: string; sdp?: string; candidate?: string } // M3
  | { kind: "demote"; transferId: string; lastVerifiedChunk: number }; // M3

/**
 * Every `kind` in the `FileCtrl` union above, as runtime data.
 *
 * Lives HERE rather than in the parity test that consumes it, and that placement is the whole
 * point. `packages/core`'s tsconfig and tsconfig.typecheck.json both exclude
 * `src/**\/__tests__/**` and `src/**\/*.test.ts`, and vitest strips types without checking
 * them — so a compile-time device sitting in a test file is checked by NOTHING. That is the
 * same cannot-fail defect this replaces, just spelled differently.
 *
 * `Record<FileCtrl["kind"], true>` makes drift a compile error in both directions: a newly
 * added arm is a missing property, a renamed or deleted one is an excess property. Note that
 * `isFileCtrl`'s switch cannot do this job — it branches on an `unknown` value, so TypeScript
 * enforces no exhaustiveness there.
 */
export const FILE_CTRL_KIND_COVERAGE: Record<FileCtrl["kind"], true> = {
  offer: true,
  accept: true,
  decline: true,
  offer_received: true,
  progress: true,
  pause: true,
  resume: true,
  cancel: true,
  complete: true,
  error: true,
  p2p_signal: true,
  demote: true,
};

/** Derived from the map above — deliberately never written out a second time. */
export const FILE_CTRL_KINDS = Object.keys(FILE_CTRL_KIND_COVERAGE) as FileCtrl["kind"][];

/**
 * The decline ctrl's `reason`. Closed on the TS side; Kotlin's `Protocol.kt`
 * carries it as a plain nullable String. `"unsupported"` is the catch-all —
 * see `normalizeFtDeclineReason`.
 */
export type FtDeclineReason = "user" | "quota" | "unsupported";

export type FtErrorCode =
  | "CHUNK_AUTH_FAIL"
  | "HASH_MISMATCH"
  | "STORAGE_FULL"
  | "EXPIRED"
  | "QUOTA_EXCEEDED"
  | "INTERNAL";

/**
 * Serialized-manifest byte ceiling (validateManifest). Not part of the §3.1
 * wire constants. Scales with MAX_FILES so a legitimate N-file manifest fits
 * while still bounding a pathological one: each FileEntry serializes to a few
 * hundred bytes (36-char UUID fileId + 64-hex sha256 + name/mime/size/
 * chunkCount), so ~512 B/file leaves generous room for names and the base
 * covers the manifest envelope fields. The result (~132 KB at MAX_FILES=256)
 * stays well under the /v1/messages 512 KB body limit that carries the
 * encrypted offer.
 */
const MANIFEST_MAX_BYTES = 1024 + MAX_FILES * 512;

// @reflecto/core's tsconfig is deliberately lib-neutral (no DOM, no Node
// types — see the package charter), so `console` has no ambient declaration
// here even though every runtime this package ships to (browser page, MV3
// SW, offscreen document, Node CLI/server) provides it. Declared minimally
// for the coercion breadcrumb below.
declare const console: { warn(message: string): void };

const FT_ERROR_CODES = [
  "CHUNK_AUTH_FAIL",
  "HASH_MISMATCH",
  "STORAGE_FULL",
  "EXPIRED",
  "QUOTA_EXCEEDED",
  "INTERNAL",
] as const satisfies readonly FtErrorCode[];

/**
 * Forward-compat normalization for the error ctrl's `code` (issue #818 Part
 * 5). The closed `FtErrorCode` enum lives on a wire shared by independently
 * updated clients: rejecting an unrecognised code would DISCARD the entire
 * error ctrl, turning a newer peer's specific failure into the exact silent
 * 1h hang #818 fixed — strictly worse than degrading to `INTERNAL`. So an
 * unknown (but string) code is accepted and coerced to `"INTERNAL"`; every
 * downstream consumer keeps a sound `FtErrorCode`. The Kotlin side needs no
 * mirror: `FileCtrlRouter.dispatchError` already passes `code` through as a
 * plain string with no enum validation. Adding a NEW code still requires the
 * Kotlin mirror + interop vectors (payload-schema-changes /
 * type-sync-check skills) — and must wait until clients carrying this
 * normalization are deployed.
 */
export function normalizeFtErrorCode(code: string): FtErrorCode {
  if ((FT_ERROR_CODES as readonly string[]).includes(code)) return code as FtErrorCode;
  // Breadcrumb (review follow-up): without this line, a coerced unknown code
  // is indistinguishable from a genuine INTERNAL when debugging a newer
  // peer. The label is peer-supplied wire metadata (never file content), but
  // it is still sanitized to word characters and truncated before logging.
  console.warn(
    `[ft] unknown error code coerced to INTERNAL: ${code.replace(/[^\w-]/g, "").slice(0, 32)}`
  );
  return "INTERNAL";
}

const FT_DECLINE_REASONS = [
  "user",
  "quota",
  "unsupported",
] as const satisfies readonly FtDeclineReason[];

/**
 * Forward-compat normalization for the decline ctrl's `reason` — the same
 * defect class `normalizeFtErrorCode` fixed above, on the one other closed
 * enum this wire carries. Rejecting an unrecognised reason DISCARDED the whole
 * decline ctrl, so a newer peer declining for a reason this build has never
 * heard of left the sender with no answer at all: it sits on its live offer
 * until the 1h expiry — the same silent hang #818 was about, and strictly
 * worse than degrading the label.
 *
 * Coercion target is `"unsupported"` because it is the only member that makes
 * no claim about WHY: `"user"` would fabricate a human tap that never
 * happened, `"quota"` a storage/limit condition, while `"unsupported"` already
 * means "this receiver will not take this transfer" — exactly what an
 * unreadable reason tells us. It is also the reason both auto-decline paths
 * (extension + web oversized-offer) already emit. No consumer branches on the
 * value today (senders map any decline to one "rejected" outcome), so this
 * only decides the label a future UI would show.
 *
 * The Kotlin side needs no mirror: `Protocol.kt` types `reason` as a plain
 * nullable String and `FileCtrlRouter.dispatchDecline` passes it through with
 * no enum validation. Adding a NEW reason still requires both sides plus the
 * parity corpus — and must wait until clients carrying this normalization are
 * deployed.
 */
export function normalizeFtDeclineReason(reason: string): FtDeclineReason {
  if ((FT_DECLINE_REASONS as readonly string[]).includes(reason)) {
    return reason as FtDeclineReason;
  }
  // Breadcrumb, same shape as the error-code path: without it a coerced
  // unknown reason is indistinguishable from a genuine "unsupported" when
  // debugging a newer peer. Peer-supplied wire metadata (never file content),
  // still sanitized to word characters and truncated before logging.
  console.warn(
    `[ft] unknown decline reason coerced to unsupported: ${reason.replace(/[^\w-]/g, "").slice(0, 32)}`
  );
  return "unsupported";
}

/**
 * Type guard for decrypted envelopes carrying file-transfer control messages.
 * Validates `kind` against the known union and the required fields per kind;
 * unknown `kind` values return false (callers silently ignore them, same rule
 * as envelope `type`).
 */
export function isFileCtrl(msg: unknown): msg is { type: "file_ctrl"; payload: FileCtrl } {
  if (typeof msg !== "object" || msg === null) {
    return false;
  }
  const envelope = msg as Record<string, unknown>;
  if (envelope.type !== "file_ctrl") {
    return false;
  }
  if (typeof envelope.payload !== "object" || envelope.payload === null) {
    return false;
  }
  const p = envelope.payload as Record<string, unknown>;

  switch (p.kind) {
    case "offer":
      // Deliberately shallow (the full v1 shape check is validateManifest, at
      // the consumer). `protocolVersion` is NOT checked here on purpose: it is
      // reserved and unread, and a type check would hand a newer peer a way to
      // get its entire offer dropped — the defect class this file exists to
      // avoid. The reader that first branches on it validates it there.
      return typeof p.manifest === "object" && p.manifest !== null;
    case "accept":
      return (
        typeof p.transferId === "string" &&
        (p.resumeFromChunk === undefined || typeof p.resumeFromChunk === "number")
      );
    case "decline": {
      if (typeof p.transferId !== "string" || typeof p.reason !== "string") {
        return false;
      }
      // Same in-place normalization as the `error` case below, for the same
      // reason and at the same single choke point every decrypted file_ctrl
      // passes through — see normalizeFtDeclineReason's doc. Only an
      // UNRECOGNISED string is coerced; an ABSENT reason still rejects. That
      // absence stays a reject deliberately: every decline Android emits
      // carries a reason (FileTransferCoordinator.sendDecline), so the gap is
      // only in what its READER tolerates — a separate, still-recorded
      // divergence (corpus case ctrl/decline/reason-missing). Conditional
      // write, so the guard stays side-effect-free on every known-reason
      // (i.e. normal) envelope.
      const normalized = normalizeFtDeclineReason(p.reason);
      if (normalized !== p.reason) p.reason = normalized;
      return true;
    }
    case "progress":
      return (
        typeof p.transferId === "string" &&
        typeof p.fileId === "string" &&
        typeof p.chunksDone === "number"
      );
    case "pause":
    case "resume":
    case "offer_received":
      return typeof p.transferId === "string";
    case "cancel":
      return typeof p.transferId === "string" && typeof p.reason === "string";
    case "complete":
      return typeof p.transferId === "string" && typeof p.verified === "boolean";
    case "error": {
      if (
        typeof p.transferId !== "string" ||
        typeof p.code !== "string" ||
        typeof p.message !== "string"
      ) {
        return false;
      }
      // Deliberate in-place normalization (parse, don't validate): the guard
      // is the ONE choke point every decrypted file_ctrl passes through
      // (extension dispatch, web intake, CLI), so coercing here keeps the
      // `FtErrorCode` type honest for all of them without each consumer
      // re-checking. See normalizeFtErrorCode's doc for why unknown codes
      // must be accepted rather than rejected. The write is CONDITIONAL so
      // the guard stays side-effect-free on every known-code (i.e. normal)
      // envelope — a speculative caller only observes a mutation in the
      // rare coerced case, which the breadcrumb log also surfaces.
      const normalized = normalizeFtErrorCode(p.code);
      if (normalized !== p.code) p.code = normalized;
      return true;
    }
    case "p2p_signal":
      return (
        typeof p.transferId === "string" &&
        (p.sdp === undefined || typeof p.sdp === "string") &&
        (p.candidate === undefined || typeof p.candidate === "string")
      );
    case "demote":
      return typeof p.transferId === "string" && typeof p.lastVerifiedChunk === "number";
    default:
      return false;
  }
}

/**
 * Validate a v1 transfer manifest. Returns a description of the first failed
 * check, or null when the manifest is valid. Error strings never include
 * file names or other E2E content.
 *
 * This function validates untrusted wire data and must never throw on any
 * shape of input — always returns a string or null.
 */
export function validateManifest(m: TransferManifest): string | null {
  if (m.v !== FT_PROTOCOL_VERSION) {
    return "unsupported manifest version: readers must reject v !== 1";
  }
  if (!Array.isArray(m.files)) {
    return "files must be an array";
  }
  if (m.files.length < 1) {
    return "manifest must contain at least one file";
  }
  if (m.files.length > MAX_FILES) {
    return `manifest must contain at most ${MAX_FILES} files`;
  }
  if (!Array.isArray(m.recipientDeviceIds)) {
    return "recipientDeviceIds must be an array";
  }
  if (m.recipientDeviceIds.length !== 1) {
    return "v1 manifests must contain exactly one recipient";
  }
  if (m.chunkSize !== CHUNK_SIZE) {
    return `chunkSize must be ${CHUNK_SIZE}`;
  }
  // Validate every file entry: shape, size/chunkCount arithmetic, fileId
  // uniqueness, and that totalBytes equals the summed plaintext sizes. The
  // per-chunk AAD is keyed by fileId (transferId|fileId|chunkIndex|isFinal),
  // so a duplicate fileId would make two files' chunks AAD-collide — reject it.
  const seenFileIds = new Set<string>();
  let sizeSum = 0;
  for (let i = 0; i < m.files.length; i++) {
    const file = m.files[i];
    if (typeof file !== "object" || file === null) {
      return `files[${i}] must be an object`;
    }
    if (typeof file.size !== "number" || typeof file.chunkCount !== "number") {
      return `files[${i}] must have numeric size and chunkCount`;
    }
    // Lower bounds BEFORE the ceil() arithmetic: a negative size slips past it
    // (Math.ceil(-100 / 65536) === -0, and 0 === -0 in JS, so {size:-100,
    // chunkCount:0} would satisfy chunkCountOk). partPlan documents chunkCount >= 1
    // as "enforced by validateManifest" and throws otherwise — close the gap here
    // so a forged offer can't pass validation then crash part-planning downstream.
    if (file.size < 0 || file.chunkCount < 1) {
      return `files[${i}] must have non-negative size and chunkCount >= 1`;
    }
    const chunkCountOk =
      file.size === 0
        ? file.chunkCount === 1
        : file.chunkCount === Math.ceil(file.size / m.chunkSize);
    if (!chunkCountOk) {
      return `files[${i}] chunkCount must be ceil(size / chunkSize), or 1 for a 0-byte file`;
    }
    if (typeof file.fileId !== "string" || file.fileId.length === 0) {
      return `files[${i}] must have a non-empty fileId`;
    }
    if (seenFileIds.has(file.fileId)) {
      return `duplicate fileId at files[${i}]`;
    }
    seenFileIds.add(file.fileId);
    sizeSum += file.size;
  }
  if (m.totalBytes !== sizeSum) {
    return "totalBytes must equal the sum of file sizes";
  }
  if (typeof m.noncePrefix !== "string") {
    return "noncePrefix must be a string";
  }
  let noncePrefix: Uint8Array;
  try {
    noncePrefix = decodeBase64(m.noncePrefix);
  } catch {
    return "noncePrefix is not valid base64";
  }
  if (noncePrefix.length !== NONCE_PREFIX_LEN) {
    return `noncePrefix must decode to ${NONCE_PREFIX_LEN} bytes`;
  }
  if (decodeUTF8(JSON.stringify(m)).length > MANIFEST_MAX_BYTES) {
    return `serialized manifest exceeds ${MANIFEST_MAX_BYTES} bytes`;
  }
  return null;
}
